/**
 * Hand a signed-in session to a NEW tab of the app.
 *
 * Without "Remember me" the access token lives in `sessionStorage`, which a
 * browser keeps per tab — so any link the app opens in a new tab (a setup
 * step's "Set up now", a Ctrl-click) arrived with no token and the route guard
 * sent the person to the login page while they were signed in next door.
 *
 * A new tab asks the tabs already open over a same-origin `BroadcastChannel`;
 * any tab holding a token answers. The token is stored in the NEW tab's
 * `sessionStorage` too, so "not remembered" still means the session ends when
 * the last tab closes. Nothing here leaves the origin: a BroadcastChannel only
 * reaches documents of this same origin, which can already read this storage.
 */
import { getAccessToken } from './token';

const CHANNEL_NAME = 'autocampaign-auth-session';
/** Long enough for an open tab to answer; short enough to never feel like a wait. */
const ANSWER_TIMEOUT_MS = 600;

type SessionMessage =
    | { readonly type: 'session-request'; readonly id: string }
    | { readonly type: 'session-response'; readonly id: string; readonly token: string };

function isSessionMessage(value: unknown): value is SessionMessage {
    if (!value || typeof value !== 'object') return false;
    const message = value as Record<string, unknown>;
    return (
        (message.type === 'session-request' || message.type === 'session-response') &&
        typeof message.id === 'string'
    );
}

function openChannel(): BroadcastChannel | null {
    try {
        return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
    } catch {
        return null;
    }
}

/**
 * Answer other tabs' requests while this tab holds a session. Started once at
 * boot; returns a stop function (tests, hot reload).
 */
export function startSessionSharing(): () => void {
    const channel = openChannel();
    if (!channel) return () => undefined;

    channel.onmessage = (event: MessageEvent<unknown>) => {
        if (!isSessionMessage(event.data) || event.data.type !== 'session-request') return;
        const token = getAccessToken();
        if (!token) return;
        const response: SessionMessage = { type: 'session-response', id: event.data.id, token };
        channel.postMessage(response);
    };
    return () => channel.close();
}

/** Ask the open tabs for their session; `null` when none answers in time. */
export function requestSessionFromOpenTabs(timeoutMs = ANSWER_TIMEOUT_MS): Promise<string | null> {
    const channel = openChannel();
    if (!channel) return Promise.resolve(null);

    const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;

    return new Promise((resolve) => {
        const finish = (token: string | null) => {
            clearTimeout(timer);
            channel.close();
            resolve(token);
        };
        const timer = setTimeout(() => finish(null), timeoutMs);
        channel.onmessage = (event: MessageEvent<unknown>) => {
            if (
                isSessionMessage(event.data) &&
                event.data.type === 'session-response' &&
                event.data.id === id &&
                typeof event.data.token === 'string' &&
                event.data.token.length > 0
            ) {
                finish(event.data.token);
            }
        };
        const request: SessionMessage = { type: 'session-request', id };
        channel.postMessage(request);
    });
}
